import { useState, useEffect } from 'react';
import { Box, Button, Flex, Menu, Spinner, Text } from '@chakra-ui/react';
import { CaretDown } from '@phosphor-icons/react';

import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { apiGetQuestionBanks } from '@/api/services/question_bank.service';

interface QuestionBankDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectQuestionBank?: (questionBank: QuestionBank) => void;
}

export function QuestionBankDialog({
  isOpen,
  onClose,
  onSelectQuestionBank
}: QuestionBankDialogProps) {
  const [selectedQuestionBank, setSelectedQuestionBank] = useState<QuestionBank | null>(null);
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuestionBanks = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiGetQuestionBanks();
      if (response && response.data) {
        setQuestionBanks(response.data.question_banks);
      } else {
        setError('Failed to load question banks');
      }
    } catch (err) {
      setError('Failed to load question banks');
      console.error('Error fetching question banks:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchQuestionBanks();
    }
  }, [isOpen]);

  const handleSelect = () => {
    if (selectedQuestionBank) {
      onSelectQuestionBank?.(selectedQuestionBank);
    }
    onClose();
  };

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      placement="center"
      motionPreset="slide-in-bottom"
      unmountOnExit
    >
      <DialogBackdrop />
      <DialogContent maxW="800px" p="0">
        <DialogHeader p="32px 32px 0 32px">
          <DialogTitle fontSize="24px" fontWeight="bold" color="neutrals.900">
            Interview Questions
          </DialogTitle>
        </DialogHeader>

        <DialogBody p="32px">
          <Flex gap="32px" alignItems="stretch">
            <Box flex="1">
              <Text fontSize="18px" fontWeight="semibold" mb="16px">
                Import From Question Bank
              </Text>
              <Text fontSize="14px" color="neutrals.500" mb="16px">
                Import from question bank list
              </Text>

              <Field>
                {isLoading ? (
                  <Flex alignItems="center" justifyContent="center" minH="48px">
                    <Spinner size="sm" mr="12px" />
                    <Text fontSize="14px" color="neutrals.600">
                      Loading question banks...
                    </Text>
                  </Flex>
                ) : error ? (
                  <Box
                    p="12px 16px"
                    borderRadius="8px"
                    bg="error.50"
                    border="1px solid"
                    borderColor="error.200"
                  >
                    <Text fontSize="14px" color="error.700">
                      {error}
                    </Text>
                  </Box>
                ) : (
                  <Menu.Root>
                    <Menu.Trigger asChild>
                      <Button
                        variant="outline"
                        size="lg"
                        borderRadius="8px"
                        w="100%"
                        justifyContent="space-between"
                        textAlign="left"
                      >
                        <Text color={selectedQuestionBank ? 'neutrals.900' : 'neutrals.500'}>
                          {selectedQuestionBank
                            ? `${selectedQuestionBank.job_title} - ${selectedQuestionBank.category}`
                            : 'Select a question bank...'}
                        </Text>
                        <CaretDown size={16} />
                      </Button>
                    </Menu.Trigger>
                    <Menu.Positioner>
                      <Menu.Content maxH="200px" overflowY="auto">
                        {questionBanks.length === 0 ? (
                          <Box p="12px 16px">
                            <Text fontSize="14px" color="neutrals.500">
                              No question banks available
                            </Text>
                          </Box>
                        ) : (
                          questionBanks.map((questionBank) => (
                            <Menu.Item
                              key={questionBank.question_id}
                              value={questionBank.question_id}
                              fontSize="14px"
                              cursor="pointer"
                              onClick={() => setSelectedQuestionBank(questionBank)}
                            >
                              <Box>
                                <Text fontSize="16px">{questionBank.job_title}</Text>
                                <Text fontSize="14px" color="neutrals.500">
                                  {questionBank.category} • {questionBank.questions.length}{' '}
                                  questions
                                </Text>
                              </Box>
                            </Menu.Item>
                          ))
                        )}
                      </Menu.Content>
                    </Menu.Positioner>
                  </Menu.Root>
                )}
              </Field>
            </Box>
          </Flex>
        </DialogBody>

        <DialogFooter p="32px" pt="0">
          <Flex justifyContent="flex-end" gap="16px" w="100%">
            <Button
              p="12px 32px"
              fontSize="16px"
              border="1px solid"
              borderColor="primary.900"
              borderRadius="8px"
              bg="neutrals.0"
              color="primary.900"
              onClick={onClose}
              _hover={{
                bg: 'neutrals.50'
              }}
            >
              Close
            </Button>
            <Button
              p="12px 32px"
              fontSize="16px"
              borderRadius="8px"
              bg="primary.900"
              color="neutrals.0"
              disabled={!selectedQuestionBank}
              onClick={handleSelect}
              _hover={{
                bg: 'primary.700'
              }}
            >
              Select
            </Button>
          </Flex>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
